export const STARTUP_DIAGNOSTIC_STORAGE_KEY = "recipmon:startup-diagnostic";

export type StartupDiagnosticCode =
  | "chunk_load_error"
  | "runtime_bootstrap"
  | "telegram_sdk_load_timeout"
  | "telegram_detect_timeout"
  | "tonconnect_load_timeout"
  | "telegram_signin_timeout"
  | "unknown_startup_timeout";

export type StartupDiagnosticRecord = {
  code: StartupDiagnosticCode;
  message: string;
  at: number;
};

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const safeStorage = (storage?: StorageLike | null) => {
  if (storage) {
    return storage;
  }

  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const toMessage = (reason: unknown) => {
  if (typeof reason === "string") {
    return reason;
  }
  if (reason instanceof Error) {
    return reason.message || reason.name || "Unknown error";
  }
  if (reason && typeof reason === "object" && "message" in reason) {
    const candidate = (reason as { message?: unknown }).message;
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate;
    }
  }
  return String(reason ?? "Unknown startup error");
};

const buildStartupDiagnosticRecord = (
  code: StartupDiagnosticCode,
  reason: unknown,
): StartupDiagnosticRecord => ({
  code,
  message: toMessage(reason),
  at: Date.now(),
});

const writeStartupDiagnostic = (
  record: StartupDiagnosticRecord,
  storage?: StorageLike | null,
  mode: "first" | "replace" = "replace",
): StartupDiagnosticRecord | null => {
  const store = safeStorage(storage);
  if (!store) {
    return null;
  }

  try {
    if (mode === "first" && store.getItem(STARTUP_DIAGNOSTIC_STORAGE_KEY)) {
      return null;
    }

    store.setItem(STARTUP_DIAGNOSTIC_STORAGE_KEY, JSON.stringify(record));
    return record;
  } catch {
    return null;
  }
};

export const classifyStartupDiagnosticCode = (reason: unknown): StartupDiagnosticCode => {
  const message = toMessage(reason).toLowerCase();

  if (
    message.includes("chunkloaderror") ||
    message.includes("loading chunk") ||
    message.includes("failed to fetch dynamically imported module")
  ) {
    return "chunk_load_error";
  }

  if (message.includes("script error") || message.includes("failed to import")) {
    return "runtime_bootstrap";
  }

  return "runtime_bootstrap";
};

export const recordFirstStartupDiagnostic = (
  reason: unknown,
  storage?: StorageLike | null,
): StartupDiagnosticRecord | null => {
  return writeStartupDiagnostic(
    buildStartupDiagnosticRecord(classifyStartupDiagnosticCode(reason), reason),
    storage,
    "first",
  );
};

export const recordStartupDiagnostic = (
  code: StartupDiagnosticCode,
  reason: unknown,
  storage?: StorageLike | null,
) => writeStartupDiagnostic(buildStartupDiagnosticRecord(code, reason), storage, "replace");

export const recordFirstStartupDiagnosticWithCode = (
  code: StartupDiagnosticCode,
  reason: unknown,
  storage?: StorageLike | null,
) => writeStartupDiagnostic(buildStartupDiagnosticRecord(code, reason), storage, "first");

export const readStartupDiagnostic = (
  storage?: StorageLike | null,
): StartupDiagnosticRecord | null => {
  const store = safeStorage(storage);
  if (!store) {
    return null;
  }

  try {
    const raw = store.getItem(STARTUP_DIAGNOSTIC_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<StartupDiagnosticRecord>;
    if (
      typeof parsed?.code !== "string" ||
      typeof parsed?.message !== "string" ||
      typeof parsed?.at !== "number"
    ) {
      return null;
    }
    return parsed as StartupDiagnosticRecord;
  } catch {
    return null;
  }
};

export const clearStartupDiagnostic = (storage?: StorageLike | null) => {
  const store = safeStorage(storage);
  if (!store) {
    return;
  }

  try {
    store.removeItem(STARTUP_DIAGNOSTIC_STORAGE_KEY);
  } catch {
    // Ignore storage failures.
  }
};

export const formatStartupDiagnosticForDisplay = (
  record: StartupDiagnosticRecord | null,
): string | null => {
  if (!record) {
    return null;
  }

  const compactMessage = record.message.trim().replace(/\s+/g, " ").slice(0, 140);
  return `${record.code}: ${compactMessage}`;
};
